import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Link2, PackageSearch } from 'lucide-react';
import type { ProductoSinInventarioVista } from '@shared/types';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 81 — "Estos productos se venden sin descontar inventario".
 *
 * De dónde sale: la dueña dio de alta sus bebidas **dos veces sin saberlo**. Una como producto,
 * para poder venderlas; otra como ingrediente, para tener inventario. El sistema nunca las unió
 * ni se lo dijo, así que veía el inventario en verde con 10 piezas de "Coca Cola Light" y el POS
 * mostrando "Coca Light" sin existencia. Su reacción, textual: *"¿cómo demonios?"*.
 *
 * Tenía toda la razón. Para ella **Mirinda es una cosa**; que nuestro modelo la tenga partida en
 * dos es asunto nuestro. Esta pantalla existe para juntarlas en un clic.
 *
 * Por qué no lo hace solo: vincular mal es peor que no vincular. Este proyecto ya vivió el
 * "Refresco 600 ml → Frijoles Charros", que pasó meses descontando el ingrediente equivocado
 * porque el dato era sintácticamente válido. Aquí el sistema propone y una persona confirma.
 */
export function SinInventarioAviso() {
  const [items, setItems] = useState<ProductoSinInventarioVista[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  /** Cuánto se descuenta al vender uno, por producto. Para una bebida, 1 pieza. */
  const [cantidades, setCantidades] = useState<Record<number, string>>({});

  const cargar = useCallback(async () => {
    const res = await window.api.productos.sinInventario();
    if (res.ok) setItems(res.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (cargando || items.length === 0) return null;

  const vincular = async (item: ProductoSinInventarioVista, insumoId: number) => {
    setTrabajando(item.productoId);
    setError(null);
    setHecho(null);
    const texto = (cantidades[item.productoId] ?? '1').replace(',', '.');
    const cantidad = Number(texto);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      setError('¿Cuánto se descuenta al vender uno? Escribe un número mayor a cero.');
      setTrabajando(null);
      return;
    }
    const res = await window.api.productos.vincularInsumo({
      productoId: item.productoId,
      insumoId,
      cantidadPorVenta: cantidad,
    });
    setTrabajando(null);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    setHecho(
      `✓ "${res.data.productoNombre}" ahora descuenta ${res.data.cantidadPorVenta} ${res.data.unidad} de "${res.data.insumoNombre}".`,
    );
    await cargar();
  };

  return (
    <section className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h2 className="font-semibold text-amber-900">
              {items.length === 1
                ? '1 producto se vende sin descontar inventario'
                : `${items.length} productos se venden sin descontar inventario`}
            </h2>
            <p className="mt-0.5 text-sm text-amber-800">
              Se pueden vender, pero su existencia no baja y en el POS no muestran cuántos quedan.
              Suele pasar cuando el producto y el ingrediente se capturaron por separado.
            </p>
          </div>
        </div>
        <button
          onClick={() => setAbierto((v) => !v)}
          className="btn-primary min-h-[44px] shrink-0 bg-amber-600 hover:bg-amber-700"
        >
          <Link2 className="h-4 w-4" />
          {abierto ? 'Ocultar' : 'Revisarlos'}
        </button>
      </header>

      {abierto && (
        <div className="mt-4 space-y-3">
          {items.map((item) => (
            <article key={item.productoId} className="rounded-lg border border-amber-200 bg-white p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-semibold">
                  {item.productoNombre}
                  {!item.habilitado && (
                    <span className="ml-2 text-xs font-normal text-slate-500">(oculto)</span>
                  )}
                </span>
                <span className="text-xs text-slate-500">se vende por {item.unidadVenta}</span>
              </div>

              {item.candidatos.length === 0 ? (
                <p className="mt-2 flex items-start gap-2 text-sm text-slate-600">
                  <PackageSearch className="mt-0.5 h-4 w-4 shrink-0" />
                  No encontré un ingrediente parecido. Si quieres llevarle inventario, créalo
                  primero en Inventario; si no, déjalo así: se vende sin control de existencias.
                </p>
              ) : (
                <>
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div>
                      <label className="label" htmlFor={`cant-${item.productoId}`}>
                        Al vender uno, se descuenta
                      </label>
                      <input
                        id={`cant-${item.productoId}`}
                        className="input w-28"
                        inputMode="decimal"
                        value={cantidades[item.productoId] ?? '1'}
                        onChange={(e) =>
                          setCantidades((c) => ({ ...c, [item.productoId]: e.target.value }))
                        }
                      />
                    </div>
                    <p className="pb-2 text-xs text-slate-500">
                      Una bebida suele ser <b>1 pieza</b>. Un corte de 1/4 kg, <b>0.25 kg</b>.
                    </p>
                  </div>

                  <div className="mt-2 space-y-2">
                    {item.candidatos.map((c) => (
                      <div
                        key={c.insumoId}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2"
                      >
                        <div className="text-sm">
                          <span className="font-medium">{c.insumoNombre}</span>
                          <span className="ml-2 text-xs text-slate-500">
                            {c.motivo} · hay {c.stockActual} {c.unidad}
                          </span>
                        </div>
                        <button
                          onClick={() => void vincular(item, c.insumoId)}
                          className="btn-primary min-h-[44px] text-sm"
                          disabled={trabajando === item.productoId}
                        >
                          {trabajando === item.productoId ? 'Uniendo…' : 'Es éste'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </article>
          ))}

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}
          {hecho && (
            <div className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{hecho}</div>
          )}
        </div>
      )}
    </section>
  );
}
